import { supabase } from './supabase';
import { ENV } from '../config/env';

const STORAGE_BUCKET = 'user-files';

export type StorageVisibility = 'private' | 'shared' | 'public';

export interface StorageMetadataInput {
  sourceModule: string;
  docType: string;
  visibility: StorageVisibility;
  createdBy?: string;
  tags?: string[];
  [key: string]: any;
}

export interface StorageUploadParams {
  file: File;
  path: string;
  metadata: StorageMetadataInput;
}

export interface StorageUploadResponse {
  key: string;
  size: number;
  metadata: StorageMetadataInput;
}

export interface StorageListParams {
  prefix?: string;
  filters?: Record<string, string>;
}

export interface StorageListItem {
  name: string;
  id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  last_accessed_at?: string | null;
  metadata?: StorageMetadataInput | null;
  objectKey?: string;
  [key: string]: any;
}

export interface StorageListResponse {
  items: StorageListItem[];
  prefix: string;
}

async function getAuthContext() {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData?.session?.user?.id ?? null;
  const token = sessionData?.session?.access_token ?? null;
  if (!userId) {
    throw new Error('Authentication required for storage operations');
  }
  return { userId, token };
}

function buildAuthHeaders(token: string | null, extra?: Record<string, string>) {
  const headers: Record<string, string> = { ...(extra || {}) };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function uploadFile(params: StorageUploadParams): Promise<StorageUploadResponse> {
  const { file, path, metadata } = params;
  const { userId, token } = await getAuthContext();

  const finalMetadata: StorageMetadataInput = {
    ...metadata,
    createdBy: metadata.createdBy ?? userId,
  };

  const form = new FormData();
  form.set('path', path);
  form.set('file', file);
  form.set('metadata', JSON.stringify(finalMetadata));
  form.set('userId', userId);

  const res = await fetch(`${ENV.API_URL}/api/storage/upload`, {
    method: 'POST',
    headers: buildAuthHeaders(token),
    body: form,
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => '');
    throw new Error(errorText || 'Storage upload failed');
  }

  const payload = await res.json().catch(() => ({}));
  return {
    key: payload.key ?? path,
    size: typeof payload.size === 'number' ? payload.size : file.size,
    metadata: payload.metadata ?? finalMetadata,
  };
}

async function listFiles(params: StorageListParams = {}): Promise<StorageListResponse> {
  const { userId, token } = await getAuthContext();
  const url = new URL(`${ENV.API_URL}/api/storage/list`);
  url.searchParams.set('userId', userId);

  if (params.prefix) {
    url.searchParams.set('prefix', params.prefix);
  }

  if (params.filters) {
    Object.entries(params.filters).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value);
      }
    });
  }

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: buildAuthHeaders(token),
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => '');
    throw new Error(errorText || 'Failed to list storage objects');
  }

  const payload = await res.json().catch(() => ({ items: [], prefix: '' }));
  return {
    items: Array.isArray(payload.items) ? payload.items : [],
    prefix: typeof payload.prefix === 'string' ? payload.prefix : '',
  };
}

async function deleteFile(path: string, options: { recursive?: boolean } = {}): Promise<void> {
  const { userId, token } = await getAuthContext();
  const res = await fetch(`${ENV.API_URL}/api/storage/object`, {
    method: 'DELETE',
    headers: buildAuthHeaders(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ path, recursive: !!options.recursive, userId }),
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => '');
    throw new Error(errorText || 'Failed to delete storage object');
  }
}

async function downloadFile(objectKey: string): Promise<Blob> {
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .download(objectKey);

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error('No data returned while downloading storage object');
  }

  return data;
}

async function getSignedUrl(objectKey: string, expiresIn = 60): Promise<string> {
  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(objectKey, expiresIn);

  if (error || !data?.signedUrl) {
    throw error || new Error('Failed to create signed URL');
  }

  return data.signedUrl;
}

export const storage = {
  uploadFile,
  listFiles,
  deleteFile,
  downloadFile,
  getSignedUrl,
};
